import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { UserPlus, Phone, Zap, Search, CircleDot, Maximize2, Minimize2, Loader2, X, MapPin, RotateCcw } from 'lucide-react';
import ambulancePng from '../../svg/Rectangle.png';

const MAX_ETA_MINUTES = 60;

// Trip totals are for the fleet owner behind the ambulance (all of their
// ambulances), not for this single vehicle — so ambulances of one owner show
// the same figures.
const formatRupees = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const TripSummaryCell = ({ summary }) => (
  <div className="col-span-2 min-w-0 text-[11px] leading-snug text-gray-500">
    {summary ? (
      <>
        <div>Total trips: <span className="font-semibold text-gray-800">{summary.totalTrips}</span></div>
        <div>This month: <span className="font-semibold text-gray-800">{summary.thisMonthTrips}</span></div>
        <div>Total value: <span className="font-semibold text-gray-800">{formatRupees(summary.totalValue)}</span></div>
      </>
    ) : (
      <span className="text-gray-400">--</span>
    )}
  </div>
);

const CommandCenterAssignDriverCard = ({
  drivers = [],
  total = 0,
  hasSearched = false,
  searching = false,
  loadingMore = false,
  onLoadMore,
  filters = { maxEtaMinutes: 60, search: '', partnerOnly: false },
  onFiltersChange,
  hasHospital = false,
  selectedDriverId,
  assignedDriver,
  onRequestAssign,
  onRequestManualAssign,
  onRequestAutoAssign,
  assigning = false,
  locationFilters = { city: '', area: '', pincode: '' },
  onLocationFiltersChange,
  onSearchByLocation,
  activeLocationSearch = null,
  onClearLocationSearch,
  isGeocoding = false,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Close on Escape key press
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  const maxEtaMinutes = filters?.maxEtaMinutes ?? 60;
  const etaPercent = ((maxEtaMinutes - 1) / (MAX_ETA_MINUTES - 1)) * 100;
  const hasMore = drivers.length > 0 && drivers.length < total;

  const handleAutoAssign = () => {
    if (drivers.length === 0) return;
    onRequestAutoAssign?.();
  };

  const handleManualAssign = () => {
    onRequestManualAssign?.();
  };

  // Header Bar (controls, ETA slider, Partner filter, Add & Assign, Auto Assign, Fullscreen toggle)
  const renderHeaderBar = (inFullscreen = false) => (
    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-2 flex-wrap shrink-0 bg-white">
      <div className="flex items-center gap-2.5 flex-wrap">
        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold shrink-0">
          {total} Available
        </span>

        <div className="flex items-center gap-2 shrink-0">
          <label className="text-xs font-medium text-gray-500 shrink-0">ETA</label>
          <input
            type="range"
            min="1"
            max={MAX_ETA_MINUTES}
            step="1"
            value={maxEtaMinutes}
            onChange={(e) => onFiltersChange?.({ maxEtaMinutes: Number(e.target.value) })}
            className="w-20 sm:w-24 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #2563EB ${etaPercent}%, #E5E7EB ${etaPercent}%)`,
            }}
          />
          <span className="text-xs font-semibold text-blue-600 shrink-0 whitespace-nowrap">
            {maxEtaMinutes} min
          </span>
        </div>

        <label
          className={`flex items-center gap-1.5 shrink-0 text-xs font-medium ${
            hasHospital ? 'text-gray-600 cursor-pointer' : 'text-gray-300 cursor-not-allowed'
          }`}
          title={hasHospital ? undefined : 'Select a hospital to filter by partner fleet'}
        >
          <input
            type="checkbox"
            checked={!!filters.partnerOnly}
            disabled={!hasHospital}
            onChange={(e) => onFiltersChange?.({ partnerOnly: e.target.checked })}
            className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
          />
          Partner
        </label>
      </div>

      <div className="flex items-center gap-2 shrink-0 ml-auto">
        <button
          type="button"
          onClick={handleManualAssign}
          disabled={assigning}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50 text-xs font-medium transition-colors whitespace-nowrap bg-white shadow-2xs cursor-pointer disabled:opacity-50"
        >
          <UserPlus className="w-3.5 h-3.5" />
          <span>Add &amp; Assign</span>
        </button>
        <button
          type="button"
          onClick={handleAutoAssign}
          disabled={assigning}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50 text-xs font-medium transition-colors whitespace-nowrap bg-white shadow-2xs cursor-pointer disabled:opacity-50"
        >
          <Zap className="w-3.5 h-3.5" />
          <span>Auto Assign</span>
        </button>
        <button
          type="button"
          onClick={() => setIsFullscreen((prev) => !prev)}
          className="w-7 h-7 flex items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50 cursor-pointer ml-1"
          title={inFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        >
          {inFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
        </button>
        {inFullscreen && (
          <button
            type="button"
            onClick={() => setIsFullscreen(false)}
            className="w-7 h-7 flex items-center justify-center rounded-md border border-gray-200 text-gray-400 hover:text-gray-700 hover:bg-gray-50 cursor-pointer ml-0.5"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );

  // Search Bar with Text Search and City / Area / Pincode Location Filter
  const renderSearchBar = () => {
    return (
      <div className="px-4 py-2.5 border-b border-gray-100 shrink-0 bg-white space-y-2">
        {/* Text Search: Driver name, phone, plate */}
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={filters.search || ''}
            onChange={(e) => onFiltersChange?.({ search: e.target.value })}
            placeholder="Search by name, phone, plate..."
            className="w-full pl-9 pr-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Location Search Header & Active Tag */}
        <div className="pt-2 border-t border-gray-100">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-1 text-[11px] font-semibold text-gray-700">
              <MapPin className="w-3.5 h-3.5 text-blue-600 shrink-0" />
              <span>Location Filter</span>
              <span className="text-[10px] text-gray-400 font-normal">(Google Maps)</span>
            </div>
            {activeLocationSearch && (
              <button
                type="button"
                onClick={onClearLocationSearch}
                className="text-[11px] text-red-600 hover:text-red-700 font-medium flex items-center gap-0.5 cursor-pointer transition-colors"
                title="Reset to default booking pickup location"
              >
                <RotateCcw className="w-3 h-3" />
                <span>Reset Location</span>
              </button>
            )}
          </div>

          {/* 3 Input Fields: City, Area, Pincode */}
          <div className="grid grid-cols-3 gap-1.5">
            <div>
              <input
                type="text"
                placeholder="City (e.g. Surat)"
                value={locationFilters?.city || ''}
                onChange={(e) => onLocationFiltersChange?.({ city: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && onSearchByLocation?.()}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 bg-gray-50/60 text-gray-800 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <input
                type="text"
                placeholder="Area (e.g. Varachha)"
                value={locationFilters?.area || ''}
                onChange={(e) => onLocationFiltersChange?.({ area: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && onSearchByLocation?.()}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 bg-gray-50/60 text-gray-800 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <input
                type="text"
                placeholder="Pincode (395006)"
                value={locationFilters?.pincode || ''}
                onChange={(e) => onLocationFiltersChange?.({ pincode: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && onSearchByLocation?.()}
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 bg-gray-50/60 text-gray-800 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Action Row: Active badge & live search status (search runs 500ms after typing stops) */}
          <div className="mt-2 flex items-center justify-between gap-2">
            {activeLocationSearch ? (
              <div
                className="text-[11px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200/70 flex items-center gap-1 truncate max-w-[65%]"
                title={activeLocationSearch.formattedAddress || activeLocationSearch.label}
              >
                <span className="font-semibold shrink-0">Near:</span>
                <span className="truncate">{activeLocationSearch.label}</span>
              </div>
            ) : (
              <span className="text-[10px] text-gray-400">Type a city, area or pincode &mdash; results update automatically</span>
            )}

            {isGeocoding && (
              <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-blue-600">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Locating...</span>
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Fullscreen Driver Card (used in the 2-column side-by-side layout)
  const renderFullscreenDriverCard = (driver) => {
    const isSelected = driver.id === selectedDriverId;
    return (
      <div
        key={driver.id}
        className={`grid grid-cols-12 gap-2 items-center p-3 sm:p-3.5 rounded-xl border transition-all ${
          isSelected
            ? 'bg-blue-50/80 border-blue-400 ring-1 ring-blue-300 shadow-xs'
            : 'bg-white border-slate-200/90 hover:border-blue-300 hover:bg-blue-50/20 hover:shadow-2xs'
        }`}
      >
        {/* Ambulance / Fleet (col 5) */}
        <div className="col-span-4 flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-7 rounded-md bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0 p-0.5">
            <img
              src={ambulancePng}
              alt="Ambulance"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs font-bold text-gray-900 tracking-wide font-mono">
                {driver.vehiclePlate}
              </span>
              {driver.isPartnerFleet && (
                <span className="px-1.5 py-0.2 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/60">
                  Partner
                </span>
              )}
            </div>
            <div
              className="text-[11px] text-gray-500 truncate mt-0.5 cursor-help"
              title={driver.vehicleTypeFullName ? `${driver.vehicleTypeFullName} (${driver.vehicleTypeLabel}) - ${driver.vendorName}` : `${driver.vehicleTypeLabel} - ${driver.vendorName}`}
            >
              {driver.vehicleTypeLabel} - {driver.vendorName}
            </div>
            {driver.vendorPhone && (
              <a
                href={`tel:${driver.vendorPhone}`}
                className="text-[11px] text-blue-600 font-medium hover:underline flex items-center gap-1 mt-0.5"
                onClick={(e) => e.stopPropagation()}
              >
                <Phone className="w-2.5 h-2.5" />
                <span>{driver.vendorPhone}</span>
              </a>
            )}
          </div>
        </div>

        {/* Driver / Contact (col 3) */}
        <div className="col-span-2 min-w-0">
          <div className="text-xs font-semibold text-gray-800 truncate" title={driver.name}>
            {driver.name}
          </div>
          {driver.phone ? (
            <a
              href={`tel:${driver.phone}`}
              className="text-[11px] text-blue-600 font-medium hover:underline flex items-center gap-1 mt-0.5 cursor-pointer truncate"
              onClick={(e) => e.stopPropagation()}
              title={`Call driver: ${driver.phone}`}
            >
              <Phone className="w-2.5 h-2.5 shrink-0" />
              <span className="truncate">{driver.phone}</span>
            </a>
          ) : (
            <span className="text-[11px] text-gray-400 italic">No phone</span>
          )}
        </div>

        {/* Distance / ETA (col 2) */}
        <div className="col-span-2 min-w-0">
          <div className="text-xs font-bold text-gray-800">
            {driver.distanceKm != null ? `${driver.distanceKm.toFixed(1)} km` : '--'}
          </div>
          <div className="text-[11px] font-semibold text-emerald-600 mt-0.5">
            {driver.etaMinutes != null ? `${driver.etaMinutes} min` : '--'}
          </div>
        </div>

        {/* Trip Summary (fleet owner totals) */}
        <TripSummaryCell summary={driver.tripSummary} />

        {/* Assign Button (col 2) */}
        <div className="col-span-2 text-right">
          <button
            type="button"
            onClick={() => onRequestAssign(driver)}
            disabled={assigning}
            className="inline-flex items-center justify-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold shadow-xs hover:shadow transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            <CircleDot className="w-3.5 h-3.5" />
            <span>Assign</span>
          </button>
        </div>
      </div>
    );
  };

  // Normal Single-column Row (used inside standard dashboard card)
  const renderNormalDriverRow = (driver) => {
    const isSelected = driver.id === selectedDriverId;
    return (
      <div
        key={driver.id}
        className={`grid grid-cols-12 gap-2 items-center px-4 py-3 hover:bg-blue-50/30 transition-colors ${
          isSelected ? 'bg-blue-50/60' : ''
        }`}
      >
        {/* Ambulance / Fleet */}
        <div className="col-span-4 flex items-center gap-2.5 min-w-0">
          <img
            src={ambulancePng}
            alt="Ambulance"
            className="w-8 h-6 object-contain shrink-0"
          />
          <div className="min-w-0">
            <div className="text-xs font-bold text-gray-900 truncate">
              {driver.vehiclePlate}
            </div>
            <div
              className="text-[11px] text-gray-500 truncate cursor-help"
              title={driver.vehicleTypeFullName ? `${driver.vehicleTypeFullName} (${driver.vehicleTypeLabel}) - ${driver.vendorName}` : `${driver.vehicleTypeLabel} - ${driver.vendorName}`}
            >
              {driver.vehicleTypeLabel} - {driver.vendorName}
            </div>
            {driver.vendorPhone && (
              <a
                href={`tel:${driver.vendorPhone}`}
                className="text-[11px] text-blue-600 font-medium hover:underline flex items-center gap-1 mt-0.5 cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                <Phone className="w-2.5 h-2.5" />
                <span>{driver.vendorPhone}</span>
              </a>
            )}
          </div>
        </div>

        {/* Driver / Contact */}
        <div className="col-span-2 min-w-0">
          <div className="text-xs font-semibold text-gray-800 truncate">
            {driver.name}
          </div>
          {driver.phone ? (
            <a
              href={`tel:${driver.phone}`}
              className="text-[11px] text-blue-600 font-medium hover:underline flex items-center gap-1 mt-0.5 cursor-pointer truncate"
              onClick={(e) => e.stopPropagation()}
              title={`Call driver: ${driver.phone}`}
            >
              <Phone className="w-2.5 h-2.5 shrink-0" />
              <span className="truncate">{driver.phone}</span>
            </a>
          ) : (
            <span className="text-[11px] text-gray-400 italic">No phone</span>
          )}
        </div>

        {/* Distance / ETA */}
        <div className="col-span-2 min-w-0">
          <div className="text-xs font-bold text-gray-800">
            {driver.distanceKm != null ? `${driver.distanceKm.toFixed(1)} km` : '--'}
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5">
            {driver.etaMinutes != null ? `${driver.etaMinutes} min` : '--'}
          </div>
        </div>

        {/* Trip Summary (fleet owner totals) */}
        <TripSummaryCell summary={driver.tripSummary} />

        {/* Assign Button */}
        <div className="col-span-2 text-right">
          <button
            type="button"
            onClick={() => onRequestAssign(driver)}
            disabled={assigning}
            className="inline-flex items-center justify-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold shadow-xs hover:shadow transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            <CircleDot className="w-3.5 h-3.5" />
            <span>Assign</span>
          </button>
        </div>
      </div>
    );
  };

  // FULLSCREEN MODAL VIEW (covers full page, 2-column side-by-side grid)
  if (isFullscreen) {
    return createPortal(
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-xs p-3 sm:p-5"
        onClick={() => setIsFullscreen(false)}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-[1680px] h-[92vh] max-h-[92vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {renderHeaderBar(true)}
          {renderSearchBar()}

          {/* Side-by-side table headers for 2 columns (Desktop) */}
          <div className="hidden lg:grid grid-cols-2 gap-3 px-4 py-2 border-b border-gray-200 bg-gray-50/90 text-xs font-semibold text-gray-600 shrink-0">
            <div className="grid grid-cols-12 gap-2 px-3">
              <div className="col-span-4">Ambulance / Fleet</div>
              <div className="col-span-2">Driver / Contact</div>
              <div className="col-span-2">Distance / ETA</div>
              <div className="col-span-2">Trip Summary</div>
              <div className="col-span-2 text-right">Action</div>
            </div>
            <div className="grid grid-cols-12 gap-2 px-3">
              <div className="col-span-4">Ambulance / Fleet</div>
              <div className="col-span-2">Driver / Contact</div>
              <div className="col-span-2">Distance / ETA</div>
              <div className="col-span-2">Trip Summary</div>
              <div className="col-span-2 text-right">Action</div>
            </div>
          </div>

          {/* Single table header for Mobile/Tablet */}
          <div className="grid lg:hidden grid-cols-12 gap-2 px-4 py-2 border-b border-gray-100 text-xs font-semibold text-gray-600 bg-gray-50/60 shrink-0">
            <div className="col-span-4">Ambulance / Fleet</div>
            <div className="col-span-2">Driver / Contact</div>
            <div className="col-span-2">Distance / ETA</div>
            <div className="col-span-2">Trip Summary</div>
            <div className="col-span-2 text-right">Action</div>
          </div>

          {/* Two-column Driver Grid (Half on left, Half on right) */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4 bg-slate-50/40">
            {searching ? (
              <div className="py-20 flex flex-col items-center justify-center gap-2 text-xs text-gray-500">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                <span>Searching for nearby ambulances&hellip;</span>
              </div>
            ) : drivers.length === 0 ? (
              <div className="py-20 text-center text-xs text-gray-400">
                {filters.search
                  ? `No ambulances match "${filters.search}".`
                  : filters.partnerOnly
                  ? "No partner fleet ambulances available right now."
                  : `No ambulances within ${maxEtaMinutes} min ETA.`}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
                  {drivers.map((driver) => renderFullscreenDriverCard(driver))}
                </div>

                {hasMore && (
                  <div className="pt-4 pb-2">
                    <button
                      type="button"
                      onClick={onLoadMore}
                      disabled={loadingMore}
                      className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-50 text-xs font-semibold shadow-2xs transition-colors cursor-pointer"
                    >
                      {loadingMore && <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600" />}
                      {loadingMore ? 'Loading…' : `Load more (${drivers.length} of ${total})`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // NORMAL CARD VIEW (Embedded in Booking Page right stack)
  return (
    <div className="bg-white rounded-2xl shadow overflow-hidden">
      {!hasSearched ? (
        <div className="px-5 py-4">
          <h3 className="text-sm font-semibold text-gray-900">Driver</h3>
          <p className="text-xs text-gray-500 mt-0.5">No driver assigned yet</p>
          <p className="text-xs text-gray-400 mt-1">
            Fill in the booking form and click Search Ambulance to find nearby drivers for this trip.
          </p>
        </div>
      ) : (
        <>
          {renderHeaderBar(false)}
          {renderSearchBar()}

          {/* Table Header */}
          <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-gray-100 text-xs font-semibold text-gray-600 bg-gray-50/60">
            <div className="col-span-4">Ambulance / Fleet</div>
            <div className="col-span-2">Driver / Contact</div>
            <div className="col-span-2">Distance / ETA</div>
            <div className="col-span-2">Trip Summary</div>
            <div className="col-span-2 text-right"></div>
          </div>

          {/* Ambulance Rows */}
          <div className="overflow-y-auto divide-y divide-gray-100 max-h-80">
            {searching ? (
              <div className="py-8 flex items-center justify-center gap-2 text-xs text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                Searching for nearby ambulances&hellip;
              </div>
            ) : drivers.length === 0 ? (
              <div className="py-8 text-center text-xs text-gray-400">
                {filters.search
                  ? `No ambulances match "${filters.search}".`
                  : filters.partnerOnly
                  ? "No partner fleet ambulances available right now."
                  : `No ambulances within ${maxEtaMinutes} min ETA.`}
              </div>
            ) : (
              drivers.map((driver) => renderNormalDriverRow(driver))
            )}

            {hasMore && (
              <div className="p-3">
                <button
                  type="button"
                  onClick={onLoadMore}
                  disabled={loadingMore}
                  className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 text-xs font-medium transition-colors cursor-pointer"
                >
                  {loadingMore && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {loadingMore ? 'Loading…' : `Load more (${drivers.length} of ${total})`}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CommandCenterAssignDriverCard;
